"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

/**
 * PagemakerInAllCantons — Section 3: 26-Kantone-Grid
 * Drop-in für: src/pages/PagemakerInAllCantons/sections/KantoneGrid.tsx
 *
 * Jeder Kanton hat eine eigene Card mit anchor-id (für Footer-Links wie
 * /pagemaker24-kantone#zuerich), kantonsspezifischer Beschreibung und
 * gemeinsamem CTA.
 */

type Canton = {
  name: string;
  slug: string;
  capital: string;
  region: "Deutschschweiz" | "Romandie" | "Tessin";
  note: string;
};
const cantons: Canton[] = [{
  name: "Zürich",
  slug: "zuerich",
  capital: "Zürich",
  region: "Deutschschweiz",
  note: "Wirtschaftszentrum der Schweiz. Von Coiffeuren in der City bis Restaurants in Winterthur — die volle KMU-Vielfalt."
}, {
  name: "Bern",
  slug: "bern",
  capital: "Bern",
  region: "Deutschschweiz",
  note: "Bundesstadt mit grossflächigem Kanton. Handwerker im Emmental, Cafés in Thun, Berater in der Hauptstadt — wir bedienen alle."
}, {
  name: "Luzern",
  slug: "luzern",
  capital: "Luzern",
  region: "Deutschschweiz",
  note: "Tourismus-Hotspot mit lebhaftem KMU-Sektor. Restaurants, Hotellerie und Handwerk dominieren — alle brauchen eine starke Online-Präsenz."
}, {
  name: "Uri",
  slug: "uri",
  capital: "Altdorf",
  region: "Deutschschweiz",
  note: "Bergiger Innerschweizer Kanton. Tourismus, Handwerk und Landwirtschaft — wir bringen Ihre Schweizer Identität online."
}, {
  name: "Schwyz",
  slug: "schwyz",
  capital: "Schwyz",
  region: "Deutschschweiz",
  note: "Wirtschaftsfreundlicher Kanton mit vielen Unternehmenssitzen. KMU profitieren von schneller Online-Sichtbarkeit."
}, {
  name: "Obwalden",
  slug: "obwalden",
  capital: "Sarnen",
  region: "Deutschschweiz",
  note: "Idyllische Innerschweiz. Tourismus, Hotellerie und Handwerk — Online gefunden werden ist hier entscheidend."
}, {
  name: "Nidwalden",
  slug: "nidwalden",
  capital: "Stans",
  region: "Deutschschweiz",
  note: "Klein aber wirtschaftsstark. KMU im Bereich Industrie, Tourismus und Beratung — bauen Sie Ihre Online-Marke."
}, {
  name: "Glarus",
  slug: "glarus",
  capital: "Glarus",
  region: "Deutschschweiz",
  note: "Industrie- und Naturkanton. Vom Familienbetrieb bis zur Tourismus-Region — wir bringen Ihr KMU online."
}, {
  name: "Zug",
  slug: "zug",
  capital: "Zug",
  region: "Deutschschweiz",
  note: "Internationaler Wirtschaftshub. Ob Treuhand, IT oder lokales Gewerbe — Ihre Website auf Schweizer Niveau."
}, {
  name: "Freiburg",
  slug: "freiburg",
  capital: "Freiburg",
  region: "Deutschschweiz",
  note: "Zweisprachiger Kanton (DE/FR). Wir liefern Websites, die beiden Zielgruppen gerecht werden — KMU bis zur Universität."
}, {
  name: "Solothurn",
  slug: "solothurn",
  capital: "Solothurn",
  region: "Deutschschweiz",
  note: "Industrie und Handwerk im Mittelland. Maler, Schreiner, Treuhänder, Coiffeure — bringen Sie Ihre Leistungen online."
}, {
  name: "Basel-Stadt",
  slug: "basel-stadt",
  capital: "Basel",
  region: "Deutschschweiz",
  note: "Pharma-Hub mit lebhaftem KMU-Sektor. Restaurants, Beratungen, Designer — Ihre Stadt verdient digitale Präsenz."
}, {
  name: "Basel-Landschaft",
  slug: "basel-landschaft",
  capital: "Liestal",
  region: "Deutschschweiz",
  note: "Industrie- und Handwerkskanton rund um Basel. KMU profitieren von lokaler Google-Sichtbarkeit in der Region Nordwestschweiz."
}, {
  name: "Schaffhausen",
  slug: "schaffhausen",
  capital: "Schaffhausen",
  region: "Deutschschweiz",
  note: "Grenzkanton mit starkem Industrie- und Handwerkssektor. Tourismus rund um den Rheinfall — bringen Sie Ihre Marke online."
}, {
  name: "Appenzell Ausserrhoden",
  slug: "appenzell-ausserrhoden",
  capital: "Herisau",
  region: "Deutschschweiz",
  note: "Hügeliger Ostschweizer Kanton. Familienbetriebe und Tourismus — wir bringen die Schweizer Tradition aufs Web."
}, {
  name: "Appenzell Innerrhoden",
  slug: "appenzell-innerrhoden",
  capital: "Appenzell",
  region: "Deutschschweiz",
  note: "Kleinster Kanton mit grosser Identität. Handwerk und Tourismus — eine starke Website hilft Ihnen weit über die Kantonsgrenze hinaus."
}, {
  name: "St. Gallen",
  slug: "st-gallen",
  capital: "St. Gallen",
  region: "Deutschschweiz",
  note: "Unser Heimkanton. Vom Bodenseeufer bis ins Toggenburg — wir kennen die KMU-Landschaft persönlich."
}, {
  name: "Graubünden",
  slug: "graubuenden",
  capital: "Chur",
  region: "Deutschschweiz",
  note: "Dreisprachiger Bergkanton. Hotellerie, Tourismus, Handwerk — Schweizer Hosting, mehrsprachige Texte, lokale SEO."
}, {
  name: "Aargau",
  slug: "aargau",
  capital: "Aarau",
  region: "Deutschschweiz",
  note: "Industrie- und Mittelland-Kanton. Maler, Sanitär, Treuhand — bringen Sie Ihren Betrieb auf die erste Google-Seite."
}, {
  name: "Thurgau",
  slug: "thurgau",
  capital: "Frauenfeld",
  region: "Deutschschweiz",
  note: "Landwirtschaft, Industrie, Tourismus am Bodensee. KMU-stark, internetbedürftig — wir liefern in einer Stunde."
}, {
  name: "Tessin",
  slug: "tessin",
  capital: "Bellinzona",
  region: "Tessin",
  note: "Italienischsprachige Schweiz. Tourismus, Gastro, Architektur — wir liefern auch zweisprachige Websites (IT/DE)."
}, {
  name: "Waadt",
  slug: "waadt",
  capital: "Lausanne",
  region: "Romandie",
  note: "Romandie-Kanton mit Lausanne als Wirtschaftszentrum. Restaurants, Coaching, Immobilien — wir liefern auch auf Französisch."
}, {
  name: "Wallis",
  slug: "wallis",
  capital: "Sitten",
  region: "Romandie",
  note: "Tourismus-Kanton mit zwei Sprachen (DE/FR). Hotellerie, Bergführer, Restaurants — wir sind zweisprachig zur Stelle."
}, {
  name: "Neuenburg",
  slug: "neuenburg",
  capital: "Neuenburg",
  region: "Romandie",
  note: "Uhrenindustrie und KMU-Vielfalt. Französischsprachiger Kanton — Schweizer Hosting, Schweizer Support."
}, {
  name: "Genf",
  slug: "genf",
  capital: "Genf",
  region: "Romandie",
  note: "Internationale Stadt mit lebhaftem Restaurant- und Beratungs-Sektor. Französischsprachige Websites mit Schweizer Qualität."
}, {
  name: "Jura",
  slug: "jura",
  capital: "Delsberg",
  region: "Romandie",
  note: "Jüngster Schweizer Kanton mit starkem Handwerk. KMU im Uhrenbau, Landwirtschaft, Gastro — wir bringen Sie online."
}];
const regionColor = {
  Deutschschweiz: "bg-blue-100 text-blue-700",
  Romandie: "bg-green-100 text-green-700",
  Tessin: "bg-amber-100 text-amber-700"
};
export default function KantoneGrid() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.05
  });
  return <section id="kantone-grid" className="bg-background py-20 md:py-24">
      <div ref={ref} className={`max-w-7xl mx-auto px-4 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-16">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-4 leading-tight">
            Alle 26 Schweizer Kantone — auf einen Blick
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground">
            Klicken Sie Ihren Kanton, um direkt zur passenden Beschreibung zu
            springen.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cantons.map(c => <div key={c.slug} id={c.slug} className="scroll-mt-32 bg-card border border-border rounded-xl p-6 hover:border-primary/40 hover:shadow-lg transition-all duration-300 flex flex-col">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-xl font-bold text-foreground tracking-tight">
                    {c.name}
                  </h3>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Hauptort: {c.capital}
                  </div>
                </div>
                <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full ${regionColor[c.region]}`}>
                  {c.region}
                </span>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground flex-grow mb-4">
                {c.note}
              </p>
              <a href="https://dashboard.pagemaker24.ch/sign-up" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:gap-2.5 transition-all">
                KMU in {c.name} starten
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>)}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-12">
          Alle 26 Kantone bedient — vom Bodensee bis zum Genfersee.
        </p>
      </div>
    </section>;
}